import { Component } from '@theme/component';

/**
 * Bra size calculator. Reads its size-lookup data from an embedded JSON
 * script (populated server-side by snippets/afs-calculator.liquid from the
 * `afs_banda` / `afs_copa` metaobjects) and listens on `document` for the
 * event fired by blocks/size-guide-popup.liquid's "trigger" mode (see
 * assets/size-guide.js for that contract) so it can open its parent
 * `dialog-component` on demand.
 *
 * @typedef {object} SizeEntry
 * @property {number} talla
 * @property {number} min
 * @property {number} max
 *
 * @typedef {object} CupEntry
 * @property {number} talla
 * @property {string} copa
 * @property {number} min
 * @property {number} max
 *
 * @typedef {object} Equivalent
 * @property {number} talla
 * @property {string} copa
 * @property {'snug' | 'loose'} type
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} bajoPecho
 * @property {HTMLInputElement} altoPecho
 * @property {HTMLElement} result
 * @property {HTMLElement} resultSuccess
 * @property {HTMLElement} resultBand
 * @property {HTMLElement} resultCup
 * @property {HTMLAnchorElement} [resultLink] - Absent when `show_products_link` is false (PDP dialog).
 * @property {HTMLElement} equivalences
 * @property {HTMLElement} snugCard
 * @property {HTMLElement} snugBand
 * @property {HTMLElement} snugCup
 * @property {HTMLAnchorElement} [snugLink] - Absent when `show_products_link` is false (PDP dialog).
 * @property {HTMLElement} looseCard
 * @property {HTMLElement} looseBand
 * @property {HTMLElement} looseCup
 * @property {HTMLAnchorElement} [looseLink] - Absent when `show_products_link` is false (PDP dialog).
 * @property {HTMLElement} resultError
 *
 * @extends Component<Refs>
 */
class AfsCalculator extends Component {
  /** @type {{ banda: SizeEntry[], copa: CupEntry[] }} */
  lookup = { banda: [], copa: [] };

  /**
   * Canonical event name, always listened for regardless of the trigger block's
   * (translatable) `event_name` setting — see assets/size-guide.js, which always
   * emits this name too so a per-locale translation of that setting can't
   * desync the two. `data-event-name` adds an extra name to listen for.
   */
  static CANONICAL_EVENT = 'size-guide:calculadora';

  eventName = AfsCalculator.CANONICAL_EVENT;

  requiredRefs = [
    'form',
    'bajoPecho',
    'altoPecho',
    'result',
    'resultSuccess',
    'resultBand',
    'resultCup',
    'equivalences',
    'snugCard',
    'snugBand',
    'snugCup',
    'looseCard',
    'looseBand',
    'looseCup',
    'resultError',
  ];

  /** @type {string} */
  productsUrl = '';

  /** @type {boolean} */
  filterInStock = true;

  connectedCallback() {
    super.connectedCallback();

    const raw = this.refs.data instanceof HTMLScriptElement ? this.refs.data.textContent : null;
    this.lookup = raw ? JSON.parse(raw) : { banda: [], copa: [] };

    this.eventName = this.getAttribute('data-event-name') || this.eventName;
    this.productsUrl = this.getAttribute('data-products-url') || '';
    this.filterInStock = this.getAttribute('data-filter-in-stock') !== 'false';
    this.#syncInputRanges();
    for (const name of this.#eventNames()) {
      document.addEventListener(name, this.#handleTrigger);
    }
  }

  /** Distinct event names to listen on: the configured one plus the canonical one. */
  #eventNames() {
    return this.eventName === AfsCalculator.CANONICAL_EVENT
      ? [this.eventName]
      : [this.eventName, AfsCalculator.CANONICAL_EVENT];
  }

  /**
   * Keeps the inputs' min/max in sync with the actual loaded lookup data
   * instead of hardcoded HTML attributes, so a merchant editing the
   * afs_banda / afs_copa metaobjects (e.g. extending the size range) can't
   * silently desync the native browser validation from the real data.
   */
  #syncInputRanges() {
    if (this.lookup.banda.length) {
      this.refs.bajoPecho.min = String(Math.min(...this.lookup.banda.map((entry) => entry.min)));
      this.refs.bajoPecho.max = String(Math.max(...this.lookup.banda.map((entry) => entry.max)));
    }
    if (this.lookup.copa.length) {
      this.refs.altoPecho.min = String(Math.min(...this.lookup.copa.map((entry) => entry.min)));
      this.refs.altoPecho.max = String(Math.max(...this.lookup.copa.map((entry) => entry.max)));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const name of this.#eventNames()) {
      document.removeEventListener(name, this.#handleTrigger);
    }
  }

  #handleTrigger = () => {
    const dialog = /** @type {{ showDialog?: () => void } | null} */ (this.closest('dialog-component'));
    dialog?.showDialog?.();
  };

  /**
   * Computes the size from the form inputs and renders the result.
   *
   * @param {SubmitEvent} event
   */
  calculate(event) {
    event.preventDefault();

    const bajoPecho = parseInt(this.refs.bajoPecho.value, 10);
    const altoPecho = parseInt(this.refs.altoPecho.value, 10);
    const resultado = this.#resolveSize(bajoPecho, altoPecho);

    this.refs.result.hidden = false;

    if (!resultado) {
      this.refs.resultSuccess.hidden = true;
      this.refs.resultError.hidden = false;
      return;
    }

    this.refs.resultError.hidden = true;
    this.refs.resultSuccess.hidden = false;

    this.#fillPill(this.refs.resultBand, resultado.talla);
    this.#fillPill(this.refs.resultCup, resultado.copa);
    if (this.refs.resultLink) {
      this.refs.resultLink.href = this.#buildProductsUrl(resultado.talla, resultado.copa);
    }

    const snug = resultado.equivalencias.find((entry) => entry.type === 'snug');
    const loose = resultado.equivalencias.find((entry) => entry.type === 'loose');

    this.refs.equivalences.hidden = !snug && !loose;

    this.refs.snugCard.hidden = !snug;
    if (snug) {
      this.#fillPill(this.refs.snugBand, snug.talla);
      this.#fillPill(this.refs.snugCup, snug.copa);
      if (this.refs.snugLink) {
        this.refs.snugLink.href = this.#buildProductsUrl(snug.talla, snug.copa);
      }
    }

    this.refs.looseCard.hidden = !loose;
    if (loose) {
      this.#fillPill(this.refs.looseBand, loose.talla);
      this.#fillPill(this.refs.looseCup, loose.copa);
      if (this.refs.looseLink) {
        this.refs.looseLink.href = this.#buildProductsUrl(loose.talla, loose.copa);
      }
    }
  }

  /**
   * @param {number} talla
   * @param {string} copa
   * @returns {string}
   */
  #buildProductsUrl(talla, copa) {
    if (!this.productsUrl) return '#';
    const params = new URLSearchParams({
      'filter.v.option.talla': String(talla),
      'filter.v.option.copa': copa,
    });
    // Optional: only surface products actually in stock in that size. Off by
    // merchant choice, since sizes with no stock would land on an empty page.
    if (this.filterInStock) params.set('filter.v.availability', '1');
    return `${this.productsUrl}?${params.toString()}`;
  }

  /**
   * @param {HTMLElement} pill
   * @param {string | number} size
   */
  #fillPill(pill, size) {
    pill.textContent = (pill.dataset.template || '%SIZE%').replace('%SIZE%', String(size));
  }

  /**
   * @param {number} bajoPecho
   * @param {number} altoPecho
   * @returns {{ talla: number, copa: string, equivalencias: Equivalent[] } | null}
   */
  #resolveSize(bajoPecho, altoPecho) {
    if (!bajoPecho || !altoPecho) return null;

    const bandaEntry = this.lookup.banda.find((entry) => bajoPecho >= entry.min && bajoPecho <= entry.max);
    if (!bandaEntry) return null;

    const { talla } = bandaEntry;
    const copaEntry = this.lookup.copa.find(
      (entry) => entry.talla === talla && altoPecho >= entry.min && altoPecho <= entry.max
    );
    if (!copaEntry) return null;

    const { copa } = copaEntry;
    return { talla, copa, equivalencias: this.#resolveEquivalents(talla, copa) };
  }

  /**
   * Sister sizes: "snug" trades a smaller band for one cup letter up (tighter
   * around the ribcage); "loose" trades a bigger band for one cup letter down
   * (looser around the ribcage). 80A and 130K sit at the edge of the catalog
   * with no equivalent; sizes above 130 fall outside the equivalence range
   * entirely.
   *
   * @param {number} talla
   * @param {string} copa
   * @returns {Equivalent[]}
   */
  #resolveEquivalents(talla, copa) {
    /** @type {Equivalent[]} */
    const resultado = [];
    const copaCode = copa.charCodeAt(0);

    if ((talla === 80 && copa === 'A') || (talla === 130 && copa === 'K')) return resultado;
    if (talla > 130) return resultado;

    if (talla >= 85 && talla <= 125) {
      if (copaCode >= 66 && copaCode <= 74) {
        resultado.push({ talla: talla + 5, copa: String.fromCharCode(copaCode - 1), type: 'loose' });
        resultado.push({ talla: talla - 5, copa: String.fromCharCode(copaCode + 1), type: 'snug' });
      } else if (copaCode === 65) {
        resultado.push({ talla: talla - 5, copa: String.fromCharCode(copaCode + 1), type: 'snug' });
      } else if (copaCode === 75) {
        resultado.push({ talla: talla + 5, copa: String.fromCharCode(copaCode - 1), type: 'loose' });
      }
    }

    if (talla === 80 && copaCode >= 66) {
      resultado.push({ talla: 85, copa: String.fromCharCode(copaCode - 1), type: 'loose' });
    }

    if (talla === 130 && copaCode <= 74) {
      resultado.push({ talla: 125, copa: String.fromCharCode(copaCode + 1), type: 'snug' });
    }

    return resultado;
  }
}

if (!customElements.get('afs-calculator')) {
  customElements.define('afs-calculator', AfsCalculator);
}
